/** How the API judged one row of an uploaded sheet. */
export const BULK_STATUSES = ["VALID", "INVALID", "DUPLICATE"] as const;

export type BulkStatus = (typeof BULK_STATUSES)[number];

/** What every feature's sheet row has, whatever its columns. */
export interface BulkRowBase {
  /** The row's number on the sheet; the header is row 1. */
  readonly row: number;
  readonly status: BulkStatus;
  /** Why the row can't be imported, translated; empty when valid. */
  readonly errors: readonly string[];
}

/** A bulk preview: every record of a sheet checked, nothing created.
 *  `totalRecords` = valid + invalid + duplicate. */
export interface BulkPreview<Row> {
  readonly totalRecords: number;
  readonly validRecords: number;
  readonly invalidRecords: number;
  readonly duplicateRecords: number;
  readonly rows: readonly Row[];
}

/** A bulk import: the rows still valid were created. */
export interface BulkImportResult<Row> {
  readonly totalRecords: number;
  readonly createdRecords: number;
  readonly failedRecords: number;
  readonly failedRows: readonly Row[];
  /** e.g. "96 POIs were successfully imported. 4 records could not be
   *  imported.", in the request language. */
  readonly message?: string;
}

/** The Check step's outcome: every record checked, or the file refused as a
 *  whole with the API's reason (wrong template, missing columns, no or too
 *  many records, too large, not `.xlsx`), shown above the upload area. */
export type BulkPreviewOutcome<Row> =
  | { readonly kind: "checked"; readonly preview: BulkPreview<Row> }
  | { readonly kind: "refused"; readonly message: string };

/** What a feature's API accepts: each feature passes its own, so they can
 *  differ later. */
export interface BulkLimits {
  /** The file's extension with its dot, e.g. `.xlsx`. */
  readonly fileExtension: string;
  readonly maxFileMb: number;
  readonly maxRecords: number;
}
